import styled from 'styled-components';
import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { getBuildingInvoiceAction, setBuildingInvoiceAction } from 'store/actions/propertyActions';
import {
  filterBuildingInvoices,
  paginateInvoiceRows,
  sortBuildingInvoices,
} from 'utils/buildingInvoiceDataSource';
import { InvoiceTable } from 'components/InvoiceTable';
import { InvoiceDateRangeFilterFields } from 'components/InvoiceTable/InvoiceDateRangeFilterFields';
import { Dropdown, Loading } from '@leapeasy/ui-kit';
import { openDetails } from 'store/actions/uiActions';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useListQueryState } from 'hooks/useListQueryState';
import { useClampListPagination } from 'hooks/useClampListPagination';
import { useDropFilterActiveSummaryBar } from 'hooks/useDropFilterActiveSummaryBar';
import {
  buildInvoiceListFilterChips,
  parseInvoiceListFilterChipId,
} from 'utils/listTableActiveFilterChips';
import {
  DEFAULT_INVOICE_LIST_EXTRA_FILTERS,
  INVOICE_LIST_URL_PARAM_KEYS,
  INVOICE_DISPUTED_FILTER_OPTIONS,
  areArraysEqual,
  areInvoiceDateRangeFiltersEqual,
  parseInvoiceFilters,
  serializeInvoiceFilters,
} from 'utils/invoiceListQueryFilters';
import { EMPTY_INVOICE_DATE_RANGE_FILTER } from 'utils/invoiceDateRangeFilter';

export const InvoiceTab = ({ building }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const previousBuildingIdRef = useRef(undefined);

  const {
    filter,
    setFilter,
    sortOptions,
    setSortOptions,
    pagination,
    setPagination,
    extraFilters,
    setExtraFilters,
  } = useListQueryState({
    defaultFilter: {
      options: {},
      searchText: '',
      searchPlaceholder: 'Search Invoice..',
    },
    extraFiltersParam: 'filters',
    defaultExtraFilters: DEFAULT_INVOICE_LIST_EXTRA_FILTERS,
    parseExtraFilters: parseInvoiceFilters,
    serializeExtraFilters: serializeInvoiceFilters,
  });

  const [selectedPayment, setSelectedPayment] = useState(() =>
    Array.isArray(extraFilters?.selectedPayment) ? extraFilters.selectedPayment : [],
  );
  const [selectedInvoiceStatus, setSelectedInvoiceStatus] = useState(() =>
    Array.isArray(extraFilters?.selectedInvoiceStatus) ? extraFilters.selectedInvoiceStatus : [],
  );
  const [selectedDueDateRange, setSelectedDueDateRange] = useState(
    () => extraFilters?.selectedDueDateRange || { ...EMPTY_INVOICE_DATE_RANGE_FILTER },
  );
  const [selectedInvoiceDateRange, setSelectedInvoiceDateRange] = useState(
    () => extraFilters?.selectedInvoiceDateRange || { ...EMPTY_INVOICE_DATE_RANGE_FILTER },
  );
  const [selectedDisputed, setSelectedDisputed] = useState(() =>
    Array.isArray(extraFilters?.selectedDisputed) ? extraFilters.selectedDisputed : [],
  );
  const [selectedPaymentDraft, setSelectedPaymentDraft] = useState(() =>
    Array.isArray(extraFilters?.selectedPayment) ? extraFilters.selectedPayment : [],
  );
  const [selectedInvoiceStatusDraft, setSelectedInvoiceStatusDraft] = useState(() =>
    Array.isArray(extraFilters?.selectedInvoiceStatus) ? extraFilters.selectedInvoiceStatus : [],
  );
  const [selectedDueDateRangeDraft, setSelectedDueDateRangeDraft] = useState(
    () => extraFilters?.selectedDueDateRange || { ...EMPTY_INVOICE_DATE_RANGE_FILTER },
  );
  const [selectedInvoiceDateRangeDraft, setSelectedInvoiceDateRangeDraft] = useState(
    () => extraFilters?.selectedInvoiceDateRange || { ...EMPTY_INVOICE_DATE_RANGE_FILTER },
  );
  const [selectedDisputedDraft, setSelectedDisputedDraft] = useState(() =>
    Array.isArray(extraFilters?.selectedDisputed) ? extraFilters.selectedDisputed : [],
  );

  useEffect(() => {
    if (!extraFilters || typeof extraFilters !== 'object') return;
    const nextSelectedPayment = Array.isArray(extraFilters.selectedPayment)
      ? extraFilters.selectedPayment
      : [];
    const nextSelectedInvoiceStatus = Array.isArray(extraFilters.selectedInvoiceStatus)
      ? extraFilters.selectedInvoiceStatus
      : [];
    const nextSelectedDueDateRange = extraFilters.selectedDueDateRange || {
      ...EMPTY_INVOICE_DATE_RANGE_FILTER,
    };
    const nextSelectedInvoiceDateRange = extraFilters.selectedInvoiceDateRange || {
      ...EMPTY_INVOICE_DATE_RANGE_FILTER,
    };
    const nextSelectedDisputed = Array.isArray(extraFilters.selectedDisputed)
      ? extraFilters.selectedDisputed
      : [];
    setSelectedPayment((prev) =>
      areArraysEqual(prev, nextSelectedPayment) ? prev : nextSelectedPayment,
    );
    setSelectedInvoiceStatus((prev) =>
      areArraysEqual(prev, nextSelectedInvoiceStatus) ? prev : nextSelectedInvoiceStatus,
    );
    setSelectedDueDateRange((prev) =>
      areInvoiceDateRangeFiltersEqual(prev, nextSelectedDueDateRange)
        ? prev
        : nextSelectedDueDateRange,
    );
    setSelectedInvoiceDateRange((prev) =>
      areInvoiceDateRangeFiltersEqual(prev, nextSelectedInvoiceDateRange)
        ? prev
        : nextSelectedInvoiceDateRange,
    );
    setSelectedDisputed((prev) =>
      areArraysEqual(prev, nextSelectedDisputed) ? prev : nextSelectedDisputed,
    );
    setSelectedPaymentDraft((prev) =>
      areArraysEqual(prev, nextSelectedPayment) ? prev : nextSelectedPayment,
    );
    setSelectedInvoiceStatusDraft((prev) =>
      areArraysEqual(prev, nextSelectedInvoiceStatus) ? prev : nextSelectedInvoiceStatus,
    );
    setSelectedDueDateRangeDraft((prev) =>
      areInvoiceDateRangeFiltersEqual(prev, nextSelectedDueDateRange)
        ? prev
        : nextSelectedDueDateRange,
    );
    setSelectedInvoiceDateRangeDraft((prev) =>
      areInvoiceDateRangeFiltersEqual(prev, nextSelectedInvoiceDateRange)
        ? prev
        : nextSelectedInvoiceDateRange,
    );
    setSelectedDisputedDraft((prev) =>
      areArraysEqual(prev, nextSelectedDisputed) ? prev : nextSelectedDisputed,
    );
  }, [extraFilters]);

  const buildingId = building?.building_id;
  const landlordId = building?.sf_landlord_id || building?.landlord_id;
  const scopeKey = buildingId || landlordId;

  useLayoutEffect(() => {
    if (!scopeKey) return;
    if (previousBuildingIdRef.current !== scopeKey) {
      if (previousBuildingIdRef.current !== undefined) {
        const next = new URLSearchParams(searchParams);
        INVOICE_LIST_URL_PARAM_KEYS.forEach((key) => next.delete(key));
        setSearchParams(next, { replace: true });
      }
      previousBuildingIdRef.current = scopeKey;
    }
  }, [scopeKey, searchParams, setSearchParams]);

  const fetchBuildingInvoices = useCallback(() => {
    if (buildingId) {
      dispatch(getBuildingInvoiceAction({ buildingId }));
      return;
    }
    if (landlordId) {
      dispatch(getBuildingInvoiceAction({ landlordId }));
      return;
    }
    dispatch(setBuildingInvoiceAction({ invoiceData: [], schedule_date: null }));
  }, [dispatch, buildingId, landlordId]);

  useEffect(() => {
    fetchBuildingInvoices();
  }, [fetchBuildingInvoices]);

  const invoices = useSelector((state) => state.getIn(['property', 'invoice']));
  const invoicesJSON = invoices?.toJS?.() ?? null;
  const allInvoiceRows = invoicesJSON?.invoiceData || [];
  const scheduleDate = invoicesJSON?.schedule_date ?? null;

  const applyFunc = useCallback(() => {
    setSelectedPayment(selectedPaymentDraft);
    setSelectedInvoiceStatus(selectedInvoiceStatusDraft);
    setSelectedDueDateRange(selectedDueDateRangeDraft);
    setSelectedInvoiceDateRange(selectedInvoiceDateRangeDraft);
    setSelectedDisputed(selectedDisputedDraft);
    setExtraFilters({
      selectedPayment: selectedPaymentDraft,
      selectedInvoiceStatus: selectedInvoiceStatusDraft,
      selectedDueDateRange: selectedDueDateRangeDraft,
      selectedInvoiceDateRange: selectedInvoiceDateRangeDraft,
      selectedDisputed: selectedDisputedDraft,
    });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [
    selectedPaymentDraft,
    selectedInvoiceStatusDraft,
    selectedDueDateRangeDraft,
    selectedInvoiceDateRangeDraft,
    selectedDisputedDraft,
    setExtraFilters,
    setPagination,
  ]);

  const resetFunc = useCallback(() => {
    setSelectedPayment([]);
    setSelectedInvoiceStatus([]);
    setSelectedDueDateRange({ ...EMPTY_INVOICE_DATE_RANGE_FILTER });
    setSelectedInvoiceDateRange({ ...EMPTY_INVOICE_DATE_RANGE_FILTER });
    setSelectedDisputed([]);
    setSelectedPaymentDraft([]);
    setSelectedInvoiceStatusDraft([]);
    setSelectedDueDateRangeDraft({ ...EMPTY_INVOICE_DATE_RANGE_FILTER });
    setSelectedInvoiceDateRangeDraft({ ...EMPTY_INVOICE_DATE_RANGE_FILTER });
    setSelectedDisputedDraft([]);
    setExtraFilters({ ...DEFAULT_INVOICE_LIST_EXTRA_FILTERS });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [setExtraFilters, setPagination]);

  const filteredInvoiceRows = useMemo(
    () =>
      filterBuildingInvoices(allInvoiceRows, scheduleDate, {
        searchText: filter.searchText,
        selectedPayment,
        selectedInvoiceStatus,
        selectedDueDateRange,
        selectedInvoiceDateRange,
        selectedDisputed,
      }),
    [
      allInvoiceRows,
      scheduleDate,
      filter.searchText,
      selectedPayment,
      selectedInvoiceStatus,
      selectedDueDateRange,
      selectedInvoiceDateRange,
      selectedDisputed,
    ],
  );

  const sortedInvoiceRows = useMemo(
    () => sortBuildingInvoices(filteredInvoiceRows, sortOptions, scheduleDate),
    [filteredInvoiceRows, sortOptions, scheduleDate],
  );

  const totalItems = sortedInvoiceRows.length;

  useClampListPagination({
    pagination,
    setPagination,
    totalItems,
    isLoading: false,
  });

  const pagedInvoiceRows = useMemo(
    () => paginateInvoiceRows(sortedInvoiceRows, pagination.pageNumber, pagination.rowsPerPage),
    [sortedInvoiceRows, pagination.pageNumber, pagination.rowsPerPage],
  );

  const invoicesForTable = useMemo(() => {
    if (!invoicesJSON) return null;
    return {
      ...invoicesJSON,
      invoiceData: pagedInvoiceRows,
      total: totalItems,
    };
  }, [invoicesJSON, pagedInvoiceRows, totalItems]);

  const handleRequestExportData = useCallback(async () => sortedInvoiceRows, [sortedInvoiceRows]);

  const handleRequestFullExportData = useCallback(async () => allInvoiceRows, [allInvoiceRows]);

  const listFilterChips = useMemo(
    () =>
      buildInvoiceListFilterChips({
        selectedPayment,
        selectedInvoiceStatus,
        selectedDueDateRange,
        selectedInvoiceDateRange,
        selectedDisputed,
      }),
    [
      selectedPayment,
      selectedInvoiceStatus,
      selectedDueDateRange,
      selectedInvoiceDateRange,
      selectedDisputed,
    ],
  );

  const handleRemoveInvoiceListFilterChip = useCallback(
    (chipId) => {
      const parsed = parseInvoiceListFilterChipId(chipId);
      if (!parsed) return;

      const bumpPage = () =>
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));

      if (parsed.type === 'dueDateRange') {
        setSelectedDueDateRange({ ...EMPTY_INVOICE_DATE_RANGE_FILTER });
        setSelectedDueDateRangeDraft({ ...EMPTY_INVOICE_DATE_RANGE_FILTER });
        setExtraFilters((prev) => ({
          ...prev,
          selectedDueDateRange: { ...EMPTY_INVOICE_DATE_RANGE_FILTER },
        }));
        bumpPage();
        return;
      }
      if (parsed.type === 'invoiceDateRange') {
        setSelectedInvoiceDateRange({ ...EMPTY_INVOICE_DATE_RANGE_FILTER });
        setSelectedInvoiceDateRangeDraft({ ...EMPTY_INVOICE_DATE_RANGE_FILTER });
        setExtraFilters((prev) => ({
          ...prev,
          selectedInvoiceDateRange: { ...EMPTY_INVOICE_DATE_RANGE_FILTER },
        }));
        bumpPage();
        return;
      }
      if (parsed.type === 'payment') {
        const next = selectedPayment.filter((s) => s !== parsed.value);
        setSelectedPayment(next);
        setSelectedPaymentDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedPayment: next }));
        bumpPage();
        return;
      }
      if (parsed.type === 'invoiceStatus') {
        const next = selectedInvoiceStatus.filter((s) => s !== parsed.value);
        setSelectedInvoiceStatus(next);
        setSelectedInvoiceStatusDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedInvoiceStatus: next }));
        bumpPage();
        return;
      }
      if (parsed.type === 'disputed') {
        const next = selectedDisputed.filter((s) => s !== parsed.value);
        setSelectedDisputed(next);
        setSelectedDisputedDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedDisputed: next }));
        bumpPage();
      }
    },
    [selectedPayment, selectedInvoiceStatus, selectedDisputed, setExtraFilters, setPagination],
  );

  const { dropFilterActiveSummary, dropFilterActiveCount } = useDropFilterActiveSummaryBar(
    listFilterChips,
    handleRemoveInvoiceListFilterChip,
    resetFunc,
  );

  const onClickInvoice = (invoice) => {
    if (!invoice?.invoice_id) return;
    dispatch(
      openDetails({
        type: 'invoice',
        data: invoice,
      }),
    );
    navigate(`/invoice/${invoice.invoice_id}/detail/current`);
  };

  const tablePagination = useMemo(
    () => ({
      ...pagination,
      totalItems,
      totalPages: Math.max(1, Math.ceil(totalItems / pagination.rowsPerPage) || 1),
    }),
    [pagination, totalItems],
  );

  return (
    <Box>
      {invoicesJSON ? (
        <CustomBox>
          <InvoiceTable
            invoices={invoicesForTable}
            onRequestExportData={handleRequestExportData}
            onRequestFullExportData={handleRequestFullExportData}
            exportFullDatasetTotal={allInvoiceRows.length}
            dropFilterActiveSummary={dropFilterActiveSummary}
            dropFilterActiveCount={dropFilterActiveCount}
            filter={filter}
            dropFilter={{
              component: (
                <DropFilterContainer>
                  <Dropdown
                    onChange={(e) => {
                      setSelectedPaymentDraft(e.target.value);
                    }}
                    options={[
                      { text: 'Paid', value: '1' },
                      { text: 'Unpaid ', value: '-1' },
                    ]}
                    value={selectedPaymentDraft}
                    multiselect={true}
                    showSearch={false}
                    placeholder="All Status"
                    label="Payment"
                    style={{ width: '100%' }}
                  />
                  <Dropdown
                    onChange={(e) => {
                      setSelectedInvoiceStatusDraft(e.target.value);
                    }}
                    options={[
                      { text: 'Paid', value: 'Paid' },
                      { text: 'Viewed', value: 'Viewed' },
                      { text: 'Sent', value: 'Sent' },
                    ]}
                    value={selectedInvoiceStatusDraft}
                    multiselect={true}
                    showSearch={false}
                    placeholder="All Type"
                    label="Invoice Status"
                    style={{ width: '100%' }}
                  />
                  <InvoiceDateRangeFilterFields
                    label="Invoice date"
                    value={selectedInvoiceDateRangeDraft}
                    onChange={setSelectedInvoiceDateRangeDraft}
                  />
                  <InvoiceDateRangeFilterFields
                    label="Due date"
                    value={selectedDueDateRangeDraft}
                    onChange={setSelectedDueDateRangeDraft}
                  />
                  <Dropdown
                    onChange={(e) => {
                      setSelectedDisputedDraft(e.target.value);
                    }}
                    options={INVOICE_DISPUTED_FILTER_OPTIONS}
                    value={selectedDisputedDraft}
                    multiselect={true}
                    showSearch={false}
                    placeholder="All"
                    label="Disputed"
                    style={{ width: '100%' }}
                  />
                </DropFilterContainer>
              ),
              applyFunc: applyFunc,
              resetFunc: resetFunc,
            }}
            onChangeFilter={setFilter}
            pagination={tablePagination}
            onChangePagination={setPagination}
            sortOptions={sortOptions}
            onChangeSort={setSortOptions}
            onClickInvoice={onClickInvoice}
          />
        </CustomBox>
      ) : (
        <Loading size={'large'} />
      )}
    </Box>
  );
};

const CustomBox = styled.div`
  position: relative;
`;

const DropFilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 12px;
  * {
    box-sizing: border-box;
  }
`;
